/**
 * Phase 641 — Session Plans list page.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, ClipboardCheck, Timer, Copy, Layers } from 'lucide-react';
import { toast } from 'sonner';

export default function SessionPlansListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/session-plans');
      setRows(r.data.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load session plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleDuplicate = useCallback(async (id, evt) => {
    evt?.preventDefault();
    evt?.stopPropagation();
    try {
      const r = await api.post(`/session-plans/${id}/duplicate`);
      toast.success(`Duplicated as "${r.data.title}"`);
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Duplicate failed');
    }
  }, [fetchList]);

  return (
    <div className="space-y-6" data-testid="session-plans-list">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-primary" /> Session Plans
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Compose a training session by dragging drills into an ordered flow. Reuse across the season.
          </p>
        </div>
        <Link to="/session-plans/new">
          <Button data-testid="new-session-plan-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New plan
          </Button>
        </Link>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!loading && rows.length === 0 && (
        <Card className="border-dashed" data-testid="plans-empty">
          <CardContent className="p-8 text-center">
            <Layers className="h-8 w-8 text-slate-400 mx-auto mb-2" />
            <p className="font-medium">No session plans yet</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              A plan is a reusable stack of drills. Build once, run every Tuesday — no more scribbling on the back of a printout.
            </p>
            <Link to="/session-plans/new">
              <Button className="mt-4" data-testid="empty-new-plan-btn">Build your first plan →</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {rows.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map(plan => (
            <Link to={`/session-plans/${plan.id}`} key={plan.id} className="block group"
                  data-testid={`plan-row-${plan.id}`}>
              <Card className="hover:shadow-md hover:-translate-y-px transition-all h-full">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold group-hover:text-primary transition-colors truncate">
                      {plan.title}
                    </h3>
                    <button onClick={(e) => handleDuplicate(plan.id, e)}
                            className="text-slate-400 hover:text-primary flex-shrink-0"
                            aria-label="Duplicate plan"
                            data-testid={`duplicate-${plan.id}`}>
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {plan.description && (
                    <p className="text-xs text-slate-600 line-clamp-2">{plan.description}</p>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t text-[11px]">
                    <span className="text-slate-500">
                      {(plan.blocks || []).length} block{(plan.blocks || []).length === 1 ? '' : 's'}
                    </span>
                    <span className="flex items-center gap-1 tabular-nums font-mono font-semibold">
                      <Timer className="h-3 w-3 text-primary" />
                      {plan.total_duration_minutes || 0}m
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
